import React, { useState } from 'react';
import { CheckoutData } from '../../pages/checkout/HostingCheckout';
import { HostingPackage } from '../../services/hostingService';

interface PlanStepProps {
  data: CheckoutData;
  package: HostingPackage;
  onNext: (data: Partial<CheckoutData>) => void;
  onBack: () => void;
  hideAddDomain?: boolean;
}

/** Safe number coercion for prices that may arrive as strings/null. */
const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n: number) => Math.round(n * 100) / 100;
const money = (v: unknown) => num(v).toFixed(2);

/**
 * Pricing contract (shared with the server, which rejects mismatched totals):
 *   monthly  = price_monthly
 *   yearly   = price_yearly when it is a positive number,
 *              else round(price_monthly × 12 × 0.85, 2)
 *   domain   = register → price from the availability check (DomainStep)
 *              transfer → 12.99 (hosting only; hidden for email)
 *              existing → 0
 *   total    = servicePrice + domainPrice   (no separate discount line)
 */
const PlanStep: React.FC<PlanStepProps> = ({ data, package: pkg, onNext, onBack, hideAddDomain = false }) => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>(data.billingCycle || 'monthly');

  const pkgType = String((pkg as any)?.type || 'hosting');
  const isEmail = pkgType === 'email';

  const monthlyPrice = num(pkg.price_monthly);
  const yearlyListed = num(pkg.price_yearly);
  const yearlyPrice = yearlyListed > 0 ? yearlyListed : round2(monthlyPrice * 12 * 0.85);
  const yearlyMonthlyEquivalent = round2(monthlyPrice * 12);
  const yearlySavings = Math.max(0, round2(yearlyMonthlyEquivalent - yearlyPrice));
  const savingsPct = yearlyMonthlyEquivalent > 0 ? Math.round((yearlySavings / yearlyMonthlyEquivalent) * 100) : 0;

  // Domain price depends on the action chosen on the Domain step.
  const domainPrice = (() => {
    if (data.domainAction === 'register') {
      // DomainStep stores the availability-check price here. Fall back to
      // the list price only when the check never ran.
      const p = num(data.pricing?.domainPrice);
      return p > 0 ? p : 12.99;
    }
    if (data.domainAction === 'transfer' && !isEmail) return 12.99;
    return 0;
  })();

  const servicePriceFor = (cycle: 'monthly' | 'yearly') => (cycle === 'yearly' ? yearlyPrice : monthlyPrice);
  const calculateTotal = (cycle: 'monthly' | 'yearly') => round2(servicePriceFor(cycle) + domainPrice);

  const handleNext = () => {
    const servicePrice = servicePriceFor(billingCycle);
    const total = calculateTotal(billingCycle);

    // Built as a plain object (not an inline literal) so the extra
    // compatibility fields don't trip TS excess-property checks against
    // the shared CheckoutData.pricing shape.
    const pricing: any = {
      servicePrice,
      emailPrice: isEmail ? servicePrice : 0,
      hostingPrice: isEmail ? 0 : servicePrice,
      vpsPrice: pkgType === 'vps' ? servicePrice : 0,
      domainPrice,
      discount: 0,
      total,
      yearlySavings: billingCycle === 'yearly' ? yearlySavings : 0,
    };

    onNext({ billingCycle, pricing });
  };

  return (
    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
      {/* Glossy overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
      
      <div className="max-w-2xl mx-auto relative z-10">
        {/* Badge */}
        <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
          <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
          <span className="text-sm font-semibold text-gray-700">Step 2: Billing Plan</span>
        </div>
        
        <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
          Choose Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Billing Plan</span>
        </h2>
        
        <fieldset className="space-y-4 mb-8">
          <legend className="sr-only">Billing cycle</legend>
          {/* Monthly Plan */}
          <label className={`block p-6 bg-gradient-to-br from-white via-gray-50 to-gray-100 border-2 rounded-xl cursor-pointer transition-all duration-200 relative overflow-hidden ${
            billingCycle === 'monthly' 
              ? 'border-orange-400 ring-4 ring-orange-100 shadow-lg' 
              : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="radio"
                  name="billingCycle"
                  value="monthly"
                  checked={billingCycle === 'monthly'}
                  onChange={() => setBillingCycle('monthly')}
                  className="h-4 w-4 text-brand-orange focus:ring-brand-orange border-gray-300"
                />
                <div className="ml-4">
                  <div className="text-lg font-semibold text-gray-900">Monthly Billing</div>
                  <div className="text-sm text-gray-500">Pay month by month, cancel anytime</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">${money(monthlyPrice)}</div>
                <div className="text-sm text-gray-500">per month</div>
              </div>
            </div>
          </label>

          {/* Yearly Plan */}
          <div className="relative">
            <label className={`block p-6 bg-gradient-to-br from-white via-gray-50 to-gray-100 border-2 rounded-xl cursor-pointer transition-all duration-200 relative ${
              billingCycle === 'yearly' 
                ? 'border-orange-400 ring-4 ring-orange-100 shadow-lg' 
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
            }`}>
              {yearlySavings > 0 && (
                <div className="absolute -top-3 -right-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg z-10">
                  Save {savingsPct}%
                </div>
              )}
            
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="radio"
                  name="billingCycle"
                  value="yearly"
                  checked={billingCycle === 'yearly'}
                  onChange={() => setBillingCycle('yearly')}
                  className="h-4 w-4 text-brand-orange focus:ring-brand-orange border-gray-300"
                />
                <div className="ml-4">
                  <div className="text-lg font-semibold text-gray-900">Annual Billing</div>
                  <div className="text-sm text-gray-500">
                    {yearlySavings > 0 ? `Pay yearly and save ${savingsPct}%` : 'Pay once a year'}
                  </div>
                  {yearlySavings > 0 && (
                    <div className="text-sm text-green-700 font-medium">
                      Save ${money(yearlySavings)} per year
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">${money(yearlyPrice)}</div>
                <div className="text-sm text-gray-500">per year</div>
                {yearlySavings > 0 && (
                  <div className="text-sm text-gray-400 line-through">${money(yearlyMonthlyEquivalent)}</div>
                )}
              </div>
            </div>
            </label>
          </div>
        </fieldset>

        {/* Order Summary */}
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{pkg.name} - {billingCycle === 'yearly' ? 'Annual' : 'Monthly'}</span>
              <span className="font-medium">${money(servicePriceFor(billingCycle))}</span>
            </div>
            
            {data.domainAction === 'register' && !hideAddDomain && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Domain Registration ({data.domainName})</span>
                <span className="font-medium">${money(domainPrice)}</span>
              </div>
            )}

            {data.domainAction === 'transfer' && !isEmail && !hideAddDomain && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Domain Transfer ({data.domainName}) + 1 year</span>
                <span className="font-medium">${money(domainPrice)}</span>
              </div>
            )}
            
            {billingCycle === 'yearly' && yearlySavings > 0 && (
              <div className="flex justify-between items-center text-green-700 text-sm">
                <span>Annual savings vs monthly (already included)</span>
                <span>${money(yearlySavings)}</span>
              </div>
            )}
            
            <div className="border-t pt-3">
              <div className="flex justify-between items-center text-lg font-bold">
                <span>Total {billingCycle === 'yearly' ? '(First Year)' : '(First Month)'}</span>
                <span className="text-brand-orange">${money(calculateTotal(billingCycle))}</span>
              </div>
            </div>
            
            <div className="text-sm text-gray-500 mt-2">
              {billingCycle === 'yearly' 
                ? `Equivalent to $${money(yearlyPrice / 12)}/month`
                : 'Billed monthly'
              }
            </div>
          </div>
        </div>

        {/* Features Included */}
        <div className="bg-blue-50 rounded-lg p-4 mb-8">
          <h4 className="font-medium text-blue-900 mb-2">What's included with your plan:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-800">
            {isEmail ? (
              <>
                <div>• {pkg.max_email_accounts === -1 ? 'Unlimited' : pkg.max_email_accounts} mailbox{pkg.max_email_accounts === 1 ? '' : 'es'} included</div>
                <div>• {pkg.disk_space_gb}GB storage per mailbox</div>
                <div>• Webmail + IMAP/SMTP/POP3 access</div>
                <div>• Spam filtering, SPF/DKIM/DMARC</div>
                <div>• Aliases &amp; catch-all forwarding</div>
                <div>• Email support</div>
              </>
            ) : (
              <>
                <div>• {pkg.disk_space_gb}GB SSD Storage</div>
                <div>• {pkg.bandwidth_gb === -1 ? 'Unlimited' : `${pkg.bandwidth_gb}GB`} Bandwidth</div>
                <div>• {pkg.max_domains === -1 ? 'Unlimited' : pkg.max_domains} Domain(s)</div>
                <div>• {pkg.max_email_accounts === -1 ? 'Unlimited' : pkg.max_email_accounts} Email Account(s)</div>
                <div>• Free SSL Certificate</div>
                <div>• 24/7 Customer Support</div>
              </>
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            type="button"
            onClick={onBack}
            className="border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 shadow-md"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-lg font-semibold hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
            <span className="relative z-10">Continue to Payment</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanStep;
